<template>
  <section class="traveler-memories">
    <div class="section-header">
      <h2 class="decorated-title"><i><b><u>Traveler Memories</u></b></i></h2>
      <p>Beautiful moments captured by our travelers</p>
    </div>
    
    <div class="memories-container">
      <button class="nav-btn prev-btn" @click="slidePrev" :disabled="isAtStart">
        &lt;
      </button>
      
      <div class="memories-slider" ref="sliderRef">
        <div class="memories-track" ref="trackRef" :style="{ transform: `translateX(${translateX}px)` }">
          <div v-for="(image, index) in displayImages" :key="index" class="memory-item">
            <img :src="image" :alt="`Traveler Memory ${index + 1}`" @load="handleImageLoaded">
          </div>
        </div>
      </div>
      
      <button class="nav-btn next-btn" @click="slideNext" :disabled="isAtEnd">
        &gt;
      </button>
    </div>
    
    <div class="slider-controls">
      <div class="slider-status">
        <button class="control-btn" @click="toggleAutoplay">
          {{ isAutoPlay ? 'Pause' : 'Play' }}
        </button>
      </div>
      
      <div class="slider-indicators">
        <div v-if="images.length <= 15">
          <span 
            v-for="(_, index) in images" 
            :key="index" 
            class="indicator" 
            :class="{ 'active': currentImageIndex === index }" 
            @click="jumpToImage(index)">
          </span>
        </div>
        <div v-else>
          <!-- Paginated indicators for large number of images -->
          <div v-html="renderPaginatedIndicators()"></div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';

// Define the image paths
const images = ref([
  '/images/memories/memory1.jpg',
  '/images/memories/memory2.jpg',
  '/images/memories/memory3.jpg',
  '/images/memories/memory4.jpg',
  '/images/memories/memory5.jpg',
  '/images/memories/memory6.jpg',
  '/images/memories/memory7.jpg',
  '/images/memories/memory8.jpg',
  '/images/memories/memory9.jpg',
  '/images/memories/memory10.jpg',
  '/images/memories/memory11.jpg',
  '/images/memories/memory12.jpg',
  '/images/memories/memory13.jpg',
  '/images/memories/memory14.jpg',
  '/images/memories/memory15.jpg',
]);

// For smooth continuous scrolling, duplicate the images
const displayImages = computed(() => [...images.value, ...images.value]);

const sliderRef = ref(null);
const trackRef = ref(null);
const translateX = ref(0);
const currentImageIndex = ref(0);
const imageWidth = ref(320); // width + margin
const isAutoPlay = ref(true);
const imagesLoaded = ref(0);
const allImagesLoaded = ref(false);
const visibleCount = ref(4); // Number of visible images at once

// Compute whether we're at start or end for button disabling
const isAtStart = computed(() => currentImageIndex.value === 0);
const isAtEnd = computed(() => currentImageIndex.value >= images.value.length - 1);

let autoPlayTimer = null;
let animationId = null;

// Handle image loaded event to track when all images are ready
const handleImageLoaded = () => {
  imagesLoaded.value++;
  if (imagesLoaded.value >= displayImages.value.length) {
    allImagesLoaded.value = true;
    startAutoplay();
  }
};

// Toggle autoplay
const toggleAutoplay = () => {
  isAutoPlay.value = !isAutoPlay.value;
  if (isAutoPlay.value) {
    startAutoplay();
  } else {
    stopAutoplay();
  }
};

// Jump to a specific image
const jumpToImage = (index) => {
  currentImageIndex.value = index;
  translateX.value = -currentImageIndex.value * imageWidth.value;
};

// Start autoplay
const startAutoplay = () => {
  if (!isAutoPlay.value || !allImagesLoaded.value) return;
  
  stopAutoplay(); // Clear any existing timer
  
  autoPlayTimer = setInterval(() => {
    slideNext();
  }, 3000);
};

// Stop autoplay
const stopAutoplay = () => {
  if (autoPlayTimer) {
    clearInterval(autoPlayTimer);
    autoPlayTimer = null;
  }
};

// Slide to previous/next set of images
const slidePrev = () => {
  // 大图片时，每次滑动一张
  let newIndex = currentImageIndex.value - 1;
  
  // Ensure we don't go below 0
  newIndex = Math.max(0, newIndex);
  
  currentImageIndex.value = newIndex;
  animateSlide(-newIndex * imageWidth.value);
};

// Slide to next/next set of images
const slideNext = () => {
  // 大图片时，每次滑动一张
  let newIndex = currentImageIndex.value + 1;
  
  // If we've reached the end, jump back to first image
  if (newIndex >= images.value.length) {
    newIndex = 0;
  }
  
  currentImageIndex.value = newIndex;
  animateSlide(-newIndex * imageWidth.value);
};

// Calculate number of visible images based on container width
const calculateVisibleCount = () => {
  if (!sliderRef.value) return;
  
  const containerWidth = sliderRef.value.offsetWidth;
  visibleCount.value = Math.floor(containerWidth / imageWidth.value);
};

// Animate slide with smooth transition
const animateSlide = (targetPosition) => {
  if (animationId) {
    cancelAnimationFrame(animationId);
  }
  
  const startPosition = translateX.value;
  const distance = targetPosition - startPosition;
  const duration = 600;
  const startTime = performance.now();
  
  const animate = (currentTime) => {
    const elapsedTime = currentTime - startTime;
    
    if (elapsedTime < duration) {
      const progress = elapsedTime / duration;
      const easeProgress = 0.5 - Math.cos(progress * Math.PI) / 2;
      translateX.value = startPosition + distance * easeProgress;
      animationId = requestAnimationFrame(animate);
    } else {
      translateX.value = targetPosition;
      animationId = null;
    }
  };
  
  animationId = requestAnimationFrame(animate);
};

// Adjust calculations on resize
const handleResize = () => {
  if (sliderRef.value && trackRef.value) {
    // Update the image width based on current view
    const memoryItem = trackRef.value.querySelector('.memory-item');
    if (memoryItem) {
      const style = window.getComputedStyle(memoryItem);
      const width = parseFloat(style.width);
      const marginRight = parseFloat(style.marginRight);
      imageWidth.value = width + marginRight;
    }
    
    // Calculate visible images count
    calculateVisibleCount();
    
    // Adjust the current position
    translateX.value = -currentImageIndex.value * imageWidth.value;
  }
};

// Handle keyboard navigation
const handleKeyNavigation = (event) => {
  if (event.key === 'ArrowLeft') {
    slidePrev();
  } else if (event.key === 'ArrowRight') {
    slideNext();
  }
};

// Set up touch events for mobile swiping
const setupTouchEvents = () => {
  let startX = 0;
  let endX = 0;
  const minSwipeDistance = 30;
  
  const handleTouchStart = (e) => {
    startX = e.touches[0].clientX;
    if (animationId) {
      cancelAnimationFrame(animationId);
    }
  };
  
  const handleTouchMove = (e) => {
    if (!startX) return;
    
    const currentX = e.touches[0].clientX;
    const diff = currentX - startX;
    
    // Add resistance to the drag
    const resistance = 0.5;
    const newPosition = -currentImageIndex.value * imageWidth.value + diff * resistance;
    
    // Prevent dragging beyond bounds
    const maxPosition = 0;
    const minPosition = -(images.value.length - 1) * imageWidth.value;
    translateX.value = Math.max(Math.min(newPosition, maxPosition), minPosition);
  };
  
  const handleTouchEnd = (e) => {
    if (!startX) return;
    
    endX = e.changedTouches[0].clientX;
    const swipeDistance = endX - startX;
    
    if (Math.abs(swipeDistance) > minSwipeDistance) {
      if (swipeDistance > 0) {
        slidePrev();
      } else {
        slideNext();
      }
    } else {
      // If swipe distance is too small, animate back to current position
      animateSlide(-currentImageIndex.value * imageWidth.value);
    }
    
    startX = 0;
  };
  
  if (sliderRef.value) {
    sliderRef.value.addEventListener('touchstart', handleTouchStart, { passive: true });
    sliderRef.value.addEventListener('touchmove', handleTouchMove, { passive: true });
    sliderRef.value.addEventListener('touchend', handleTouchEnd, { passive: true });
  }
};

// Render paginated indicators to avoid template nesting issues
const renderPaginatedIndicators = () => {
  let html = '';
  
  // First 3 indicators
  for (let i = 1; i <= 3; i++) {
    const index = i - 1;
    const isActive = currentImageIndex.value === index;
    html += `<span class="indicator${isActive ? ' active' : ''}" 
                 onclick="document.dispatchEvent(new CustomEvent('jump-to-image', {detail: {index: ${index}}}))"></span>`;
  }
  
  // Ellipsis if needed
  if (currentImageIndex.value > 4) {
    html += '<span class="indicator-ellipsis">...</span>';
  }
  
  // Current and surrounding
  if (currentImageIndex.value >= 3 && currentImageIndex.value < images.value.length - 3) {
    for (let offset of [-1, 0, 1]) {
      const index = currentImageIndex.value + offset;
      if (index >= 0 && index < images.value.length) {
        const isActive = offset === 0;
        html += `<span class="indicator${isActive ? ' active' : ''}" 
                     onclick="document.dispatchEvent(new CustomEvent('jump-to-image', {detail: {index: ${index}}}))"></span>`;
      }
    }
  }
  
  // Ellipsis if needed
  if (currentImageIndex.value < images.value.length - 5) {
    html += '<span class="indicator-ellipsis">...</span>';
  }
  
  // Last 3 indicators
  for (let i = 1; i <= 3; i++) {
    const index = images.value.length - (4 - i);
    const isActive = currentImageIndex.value === index;
    html += `<span class="indicator${isActive ? ' active' : ''}" 
                 onclick="document.dispatchEvent(new CustomEvent('jump-to-image', {detail: {index: ${index}}}))"></span>`;
  }
  
  return html;
};

// Listen for indicator clicks from rendered HTML
onMounted(() => {
  document.addEventListener('jump-to-image', (event) => {
    jumpToImage(event.detail.index);
  });
  
  window.addEventListener('resize', handleResize);
  handleResize();
  
  // If images are already cached and loaded, start autoplay
  if (imagesLoaded.value >= displayImages.value.length) {
    allImagesLoaded.value = true;
    startAutoplay();
  }
  
  // Add keyboard navigation
  window.addEventListener('keydown', handleKeyNavigation);
  
  // Add touch events for mobile swiping
  if (sliderRef.value) {
    setupTouchEvents();
  }
});

// Clean up when component is unmounted
onUnmounted(() => {
  stopAutoplay();
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('keydown', handleKeyNavigation);
  if (animationId) {
    cancelAnimationFrame(animationId);
  }
  document.removeEventListener('jump-to-image', null);
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans&family=Roboto+Slab&display=swap');

.traveler-memories {
  padding: 1rem 0;
  background-color: #f8f8f8b8;
  overflow: hidden;
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  position: relative;
}

.section-header {
  text-align: center;
  margin-bottom: 3rem;
}

.section-header h2 {
  font-size: 4rem;
  font-family: serif;
  color: var(--color-heading, #333);
  margin-bottom: 1rem;
}

.section-header p {
  font-size: 1.5rem;
  color: var(--color-text, #666);
  margin: 0 auto;
}

.memories-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 100vw;
  padding: 0 30px;
  box-sizing: border-box;
}

.memories-slider {
  width: 100%;
  overflow: hidden;
  position: relative;
  margin: 0 auto;
}

.memories-track {
  display: flex;
  transition: transform 0.6s cubic-bezier(0.23, 1, 0.32, 1);
}

.memory-item {
  flex: 0 0 auto;
  width: 500px;
  height: 350px;
  margin-right: 25px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.memory-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.memory-item:hover img {
  transform: scale(1.05);
}

.nav-btn {
  width: 60px;
  height: 60px;
  background-color: rgba(82, 66, 70, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
  opacity: 0.85;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.nav-btn:hover {
  background-color: rgba(82, 66, 70, 1);
  opacity: 1;
  transform: scale(1.05);
}

.nav-btn:active {
  transform: scale(0.95);
}

.nav-btn:disabled {
  background-color: rgba(204, 204, 204, 0.8);
  cursor: not-allowed;
  opacity: 0.5;
  box-shadow: none;
}

.prev-btn {
  margin-right: 15px;
}

.next-btn {
  margin-left: 15px;
}

.slider-controls {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 2rem;
}

.slider-status {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  gap: 15px;
}

.control-btn {
  background-color: #524246;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 8px 20px;
  font-size: 0.9rem;
  cursor: pointer;
  margin-bottom: 1rem;
  transition: background-color 0.3s ease;
}

.control-btn:hover {
  background-color: #22191b;
}

.slider-indicators {
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  max-width: 80%;
  margin: 0 auto;
}

.indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #ddd;
  cursor: pointer;
  transition: background-color 0.3s ease;
  margin-bottom: 5px;
}

.indicator.active {
  background-color: #524246;
}

.indicator-ellipsis {
  color: #888;
  margin: 0 5px;
  font-size: 0.9rem;
  display: inline-block;
  transform: translateY(-3px);
}

@media (min-width: 1800px) {
  .memory-item {
    width: 550px;
    height: 380px;
  }
}

@media (min-width: 2200px) {
  .memory-item {
    width: 600px;
    height: 420px;
  }
}

@media (max-width: 1600px) {
  .memory-item {
    width: 450px;
    height: 320px;
  }
}

@media (max-width: 1200px) {
  .memory-item {
    width: 400px;
    height: 280px;
  }
}

@media (max-width: 992px) {
  .memory-item {
    width: 450px;
    height: 320px;
  }
  
  .nav-btn {
    width: 45px;
    height: 45px;
    font-size: 1.5rem;
  }
  
  .memories-slider {
    max-width: calc(100% - 90px);
  }
}

@media (max-width: 768px) {
  .traveler-memories {
    padding: 2rem 0;
  }

  .memories-container {
    padding: 1rem;
    overflow: hidden;
  }

  .memories-slider {
    max-width: 100%;
    overflow: hidden;
  }

  .memories-track {
    display: flex;
    width: 100%;
  }

  .memory-item {
    width: 100%;
    height: 70vw;
    margin-right: 0;
    border-radius: 0;
    flex: 0 0 100%;
  }
  
  .section-header {
    margin-bottom: 1.5rem;
    padding: 0 1rem;
  }

  .section-header h2 {
    font-size: 2rem;
  }
  
  .nav-btn {
    width: 35px;
    height: 35px;
    font-size: 1.2rem;
    opacity: 0.9;
  }
  
  .prev-btn {
    margin-right: 10px;
    position: absolute;
    left: 10px;
    z-index: 3;
  }
  
  .next-btn {
    margin-left: 10px;
    position: absolute;
    right: 10px;
    z-index: 3;
  }

  .slider-controls {
    margin-top: 1rem;
    padding: 0 1rem;
  }
}

@media (max-width: 480px) {
  .traveler-memories {
    padding: 1.5rem 0;
  }

  .memory-item {
    width: 100%;
    height: 70vw;
    margin-right: 0;
  }
  
  .nav-btn {
    width: 30px;
    height: 30px;
    font-size: 1rem;
  }

  .section-header {
    padding: 0 0.5rem;
  }

  .slider-controls {
    padding: 0 0.5rem;
  }
}
</style>